import React, { useEffect, useState } from 'react';
import { UseFormResult, useServiceRef } from 'hooks';
import { Input, Label, Option, Select, Typography } from 'gd-design-library';
import { CheckoutService } from '@services/checkout';
import { CustomerInfoFormFieldNames } from '../CustomerInfoForm/form';

import styles from './AddressForm.module.scss';
import classNames from 'classnames/bind';
import { colors } from 'theme/colors';

const CN = 'address-form';
const fieldClassName = `${CN}--field`;
const fieldsRowClassName = `${CN}--fields-row`;
const stateLabelClassName = `${CN}--state-label`;

const cx = classNames.bind(styles);

interface AddressFormProps {
  formData: UseFormResult<CustomerInfoFormFieldNames>;
}

export const AddressForm: React.FC<AddressFormProps> = ({
  formData: {
    state,
    errors,
    handleChange,
    handleBlur,
    handleFocus,
    handleSelect
  }
}) => {
  const checkoutServiceRef = useServiceRef(CheckoutService);
  const [ states, setStates ] = useState<string[]>([]);

  useEffect(() => {
    let isMounted = true;
    checkoutServiceRef.current.getStatesWithTaxCategories().then(states => {
      if (isMounted) {
        setStates(states);
      }
    });

    return (): void => {
      isMounted = false;
    };
  }, []);

  return (
    <div className={ cx(CN) }>
      <div className={ cx(fieldsRowClassName) }>
        <Input
          required
          name='firstName'
          variant='text'
          label='First Name'
          value={ state.firstName }
          className={ cx(fieldClassName) }
          onBlur={ handleBlur }
          onChange={ handleChange }
          onFocus={ handleFocus }
          color={ errors.firstName.isTouched && !errors.firstName.isValid ? 'error' : 'primary' }
          helperText={ errors.firstName.isTouched && !errors.firstName?.isValid ? errors.firstName.message : '' }
        />
        <Input
          required
          name='lastName'
          variant='text'
          label='Last Name'
          value={ state.lastName }
          className={ cx(fieldClassName) }
          onBlur={ handleBlur }
          onChange={ handleChange }
          onFocus={ handleFocus }
          color={ errors.lastName.isTouched && !errors.lastName.isValid ? 'error' : 'primary' }
          helperText={ errors.lastName.isTouched && !errors.lastName.isValid ? errors.lastName.message : '' }
        />
      </div>
      <Input
        required
        name='streetName'
        variant='text'
        label='Address Line 1'
        value={ state.streetName }
        className={ cx(fieldClassName) }
        onBlur={ handleBlur }
        onChange={ handleChange }
        onFocus={ handleFocus }
        color={ errors.streetName.isTouched && !errors.streetName.isValid ? 'error' : 'primary' }
        helperText={ errors.streetName.isTouched && !errors.streetName.isValid ? errors.streetName.message : '' }
      />
      <Input
        name='building'
        variant='text'
        label='Address Line 2'
        value={ state.building }
        className={ cx(fieldClassName) }
        onBlur={ handleBlur }
        onChange={ handleChange }
        onFocus={ handleFocus }
        color={ errors.building.isTouched && !errors.building.isValid ? 'error' : 'primary' }
        helperText={ errors.building.isTouched && !errors.building.isValid ? errors.building.message : '' }
      />
      <div className={ cx(fieldsRowClassName) }>
        <Input
          required
          name='postalCode'
          variant='text'
          label='Postal Code'
          value={ state.postalCode }
          className={ cx(fieldClassName) }
          onBlur={ handleBlur }
          onChange={ handleChange }
          onFocus={ handleFocus }
          color={ errors.postalCode.isTouched && !errors.postalCode.isValid ? 'error' : 'primary' }
          helperText={ errors.postalCode.isTouched && !errors.postalCode.isValid ? errors.postalCode.message : '' }
        />
        <Input
          required
          name='city'
          variant='text'
          label='City'
          value={ state.city }
          className={ cx(fieldClassName) }
          onBlur={ handleBlur }
          onChange={ handleChange }
          onFocus={ handleFocus }
          color={ errors.city.isTouched && !errors.city.isValid ? 'error' : 'primary' }
          helperText={ errors.city.isTouched && !errors.city.isValid ? errors.city.message : '' }
        />
      </div>
      <Label className={ cx(stateLabelClassName) }>
        <Typography variant='caption' as='span' color={ colors.neutral['grey.60'] }>
          State *
        </Typography>
        <Select
          className={ cx(fieldClassName) }
          items={ states.map(st => ({ name: st, value: st })) as Option[] }
          value={ { name: state.state, value: state.state } as Option }
          onChange={ (option: Option): void => {
            const syntheticEvent = {
              target: { name: 'state', value: option.value },
              type: 'change'
            } as React.ChangeEvent<HTMLInputElement>;
            handleSelect(syntheticEvent);
          } }
          onBlur={ handleBlur }
          onFocus={ handleFocus }
          placeholder='State'
          disabled={ states.length === 0 }
          width='100%'
        />
      </Label>
      <Input
        required
        name='phone'
        variant='text'
        label='Phone'
        value={ state.phone }
        className={ cx(fieldClassName) }
        onBlur={ handleBlur }
        onChange={ handleChange }
        onFocus={ handleFocus }
        color={ errors.phone.isTouched && !errors.phone.isValid ? 'error' : 'primary' }
        helperText={ errors.phone.isTouched && !errors.phone.isValid ? errors.phone.message : '' }
      />
    </div>
  );
};
